import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Spinner,
} from '@monksflow/monks-ui';
import { ArrowRight, ChevronDown, LayoutGrid, Upload, X } from 'lucide-react';

import rateReviewIcon from '@/shared/assets/images/rate_review.svg';
import { AgentAvatar } from '@/entities/agent';
import { notificationService } from '@/lib/services/notification.service';

import type { AssetKind } from '../model/setup-form.schema';
import { useTranslation } from 'react-i18next';

const MAX_BYTES = 100 * 1024 * 1024;
const IMAGE_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'] as const;
const VIDEO_MIME_TYPES = [
  'video/mp4',
  'video/quicktime',
  'video/webm',
  'video/3gpp',
  'video/ogg',
  'video/mpeg',
] as const;
const ACCEPT = [...IMAGE_MIME_TYPES, ...VIDEO_MIME_TYPES].join(',');

type PickerMode = 'upload' | 'generate';

interface TabButtonProps {
  tab: PickerMode;
  label: string;
  icon: ReactNode;
  active: boolean;
  onSelect: () => void;
}

/** Module-scope so its identity is stable across parent re-renders (T1). */
function TabButton({ tab, label, icon, active, onSelect }: TabButtonProps) {
  return (
    <button
      type="button"
      aria-label={tab}
      aria-pressed={active}
      onClick={onSelect}
      className={`flex flex-1 items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium ${
        active ? 'bg-accent text-primary' : 'text-gray-600 dark:text-muted-foreground'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

export interface AssetPickerProps {
  value?: string;
  valueKind?: AssetKind;
  valueUrl?: string;
  isUploading: boolean;
  onFileSelected: (file: File) => void;
  onClear: () => void;
  isGenerating: boolean;
  canGenerate: boolean;
  /** Generate an asset; on success the parent selects it (same as an upload). */
  onGenerate: (input: { kind: AssetKind; prompt: string }) => Promise<void> | void;
}

export const AssetPicker = ({
  value,
  valueKind,
  valueUrl,
  isUploading,
  onFileSelected,
  onClear,
  isGenerating,
  canGenerate,
  onGenerate,
}: AssetPickerProps) => {
  const { t } = useTranslation('contentTest');

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [mode, setMode] = useState<PickerMode>('upload');
  const [fileMeta, setFileMeta] = useState<{ name: string; previewUrl: string } | null>(null);
  const [kind, setKind] = useState<AssetKind>('image');
  const [prompt, setPrompt] = useState('');

  useEffect(() => {
    return () => {
      if (fileMeta?.previewUrl) URL.revokeObjectURL(fileMeta.previewUrl);
    };
  }, [fileMeta]);

  const createPreview = (file: File): string => {
    if (typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
      return URL.createObjectURL(file);
    }
    return '';
  };

  // Reset the underlying <input>'s value so re-picking the same filename
  // after a clear (or a failed upload) still fires `onChange`.
  const resetInput = () => {
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleFiles = (file: File | null) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      notificationService.showError(t('contentTest.assetPicker.fileTooLarge'));
      return;
    }
    if (fileMeta?.previewUrl) URL.revokeObjectURL(fileMeta.previewUrl);
    setFileMeta({ name: file.name, previewUrl: createPreview(file) });
    onFileSelected(file);
    resetInput();
  };

  const handleClear = () => {
    if (fileMeta?.previewUrl) URL.revokeObjectURL(fileMeta.previewUrl);
    setFileMeta(null);
    resetInput();
    onClear();
  };

  const handleGenerate = () => {
    void onGenerate({ kind, prompt: prompt.trim() });
  };

  const openFilePicker = () => inputRef.current?.click();

  // single call site each, pure branch dispatch on `value`/`mode` —
  // inlined rather than extracted into `render*`-style functions with
  // large closures.
  let content: ReactNode;

  if (value) {
    const previewSrc = fileMeta?.previewUrl ?? valueUrl;
    const label =
      fileMeta?.name ??
      (valueKind === 'video'
        ? t('contentTest.assetPicker.videoSelected')
        : t('contentTest.assetPicker.imageSelected'));
    content = (
      <div className="flex items-center gap-3 rounded-md border border-black/5 dark:border-border-light px-3 py-2">
        {previewSrc ? (
          valueKind === 'video' ? (
            <video
              src={previewSrc}
              muted
              data-testid="asset-picker-accepted-video"
              className="size-10 shrink-0 rounded object-cover"
            >
              <track kind="captions" />
            </video>
          ) : (
            <img
              src={previewSrc}
              alt=""
              data-testid="asset-picker-accepted-image"
              className="size-10 shrink-0 rounded object-cover"
            />
          )
        ) : (
          <div className="size-10 shrink-0 rounded bg-gray-100 dark:bg-surface-raised" />
        )}
        <span className="flex-1 truncate text-sm font-semibold text-foreground">{label}</span>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={t('contentTest.assetPicker.removeSelectedAsset')}
          onClick={handleClear}
          disabled={isUploading}
          className="text-muted-foreground"
        >
          <X size={16} />
        </Button>
      </div>
    );
  } else if (mode === 'upload') {
    content = (
      <div className="flex flex-col items-center gap-4 py-6">
        <Button type="button" onClick={openFilePicker} disabled={isUploading}>
          <Upload size={16} className="mr-1.5" />
          {t('contentTest.assetPicker.upload')}
        </Button>
        <p className="text-xs text-muted-foreground">
          {t('contentTest.assetPicker.imageOrVideoMax100')}
        </p>
        {isUploading && (
          <p className="text-xs text-muted-foreground">{t('contentTest.assetPicker.uploading')}</p>
        )}
      </div>
    );
  } else {
    const kindLabel =
      kind === 'video' ? t('contentTest.assetPicker.video') : t('contentTest.assetPicker.image');
    content = (
      <div className="flex w-full flex-col gap-4">
        <p className="text-sm leading-5 tracking-[-0.14px] text-foreground">
          {t('contentTest.assetPicker.monksFlowAssetCreation')}
        </p>

        <div className="flex flex-col gap-3">
          <div className="flex items-start gap-2">
            <div className="mt-2 shrink-0">
              <AgentAvatar
                agentId="content-test-asset-generator"
                agentTitle={t('contentTest.assetPicker.monksFlowAssetCreation')}
                tag="plan"
                variant="small"
              />
            </div>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={t('contentTest.assetPicker.describeYourImageOrVideo')}
              aria-label={t('contentTest.assetPicker.brief')}
              rows={1}
              disabled={isGenerating}
              className="flex-1 resize-none border-none bg-transparent text-base leading-6 text-foreground outline-none field-sizing-content placeholder:text-gray-600 dark:placeholder:text-muted-foreground disabled:opacity-50 pt-2 min-h-6"
            />
          </div>

          <div className="flex items-center justify-between mt-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="default"
                  aria-label={t('contentTest.assetPicker.selectKind')}
                  disabled={isGenerating}
                  className="w-36 justify-between"
                >
                  <span>{kindLabel}</span>
                  <ChevronDown size={16} className="text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-40">
                <DropdownMenuItem
                  onSelect={() => setKind('image')}
                  className={`data-highlighted:bg-violet-100 data-highlighted:text-violet-600 dark:data-highlighted:bg-badge-purple dark:data-highlighted:text-badge-purple-foreground ${
                    kind === 'image'
                      ? 'bg-violet-50 text-violet-600 dark:bg-badge-purple/50 dark:text-badge-purple-foreground'
                      : ''
                  }`}
                >
                  {t('contentTest.assetPicker.image')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => setKind('video')}
                  className={`data-highlighted:bg-violet-100 data-highlighted:text-violet-600 dark:data-highlighted:bg-badge-purple dark:data-highlighted:text-badge-purple-foreground ${
                    kind === 'video'
                      ? 'bg-violet-50 text-violet-600 dark:bg-badge-purple/50 dark:text-badge-purple-foreground'
                      : ''
                  }`}
                >
                  {t('contentTest.assetPicker.video')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <button
              type="button"
              data-testid="asset-picker-generate-submit"
              aria-label={t('contentTest.assetPicker.generate')}
              onClick={handleGenerate}
              disabled={!canGenerate || isGenerating || prompt.trim().length === 0}
              className="inline-flex size-8 items-center justify-center rounded-full bg-white shadow-[0px_1.76px_6.9px_rgba(0,0,0,0.1)] hover:bg-gray-100 dark:bg-surface-raised dark:hover:bg-secondary-hover disabled:pointer-events-none disabled:opacity-50"
            >
              {isGenerating ? <Spinner size="sm" /> : <ArrowRight size={20} />}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-black/5 bg-card p-3 dark:border-border-light">
      <div className="flex gap-1">
        <TabButton
          tab="upload"
          label={t('contentTest.assetPicker.upload')}
          icon={<LayoutGrid size={18} />}
          active={mode === 'upload'}
          onSelect={() => setMode('upload')}
        />
        <TabButton
          tab="generate"
          label={t('contentTest.assetPicker.generate')}
          icon={<img src={rateReviewIcon} alt="" className="size-[18px]" />}
          active={mode === 'generate'}
          onSelect={() => setMode('generate')}
        />
      </div>

      {content}

      <input
        ref={inputRef}
        data-testid="asset-picker-input"
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => handleFiles(e.target.files?.[0] ?? null)}
      />
    </div>
  );
};

AssetPicker.displayName = 'AssetPicker';
